import { usePhoneNumber, useTimeConversion } from '../../hooks';
import { useFormContext } from 'react-hook-form';
import { ErrorMessage } from '../ErrorMessage';
import { InputClearButton } from '../buttons';
import { useTextField } from 'react-aria';
import { Checkbox } from './Checkbox';
import { Tooltip } from '../tooltip';
import { useContext, useRef } from 'react';
import clsx from 'clsx';

import type { AriaCheckboxProps } from '@react-types/checkbox';
import type { AriaTextFieldOptions } from 'react-aria';
import type { FCCN } from '../../types';
import { Localization } from '../../config/localization/localization';
import { LanguageSelectorContext } from '../../context/LanguageSelectorContext';

interface IInputProps {
    checkboxOptions?: AriaCheckboxProps;
    inputOptions?: AriaTextFieldOptions<'input'>;
    tooltip?: string;
    type?: 'number' | 'phone' | 'text' | 'time' | 'zip' | 'CAzip' | 'USzip';
}

export const Input: FCCN<IInputProps> = ({ checkboxOptions, className, inputOptions, tooltip, type = 'text' }) => {
    let inputRef = useRef<HTMLInputElement | null>(null);
    const name = inputOptions?.name ?? '';

    const formContext = useFormContext();
    const formValue = formContext.watch(name);

    let { ref: formRef } = formContext.register(name);

    const { getMinutesFromHHMMSS, toHHMMSS } = useTimeConversion();
    const { formatPhoneNumber } = usePhoneNumber({});

    const { selectedLanguage } = useContext(LanguageSelectorContext);
    const localization = new Localization(selectedLanguage);

    const { labelProps, inputProps, errorMessageProps } = useTextField(
        {
            ...inputOptions,
            onBlur: (event) => {
                if (type === 'time') {
                    if (!formValue) return;
                    const seconds: number = Math.max(0, getMinutesFromHHMMSS(formValue));
                    const newTime: string = toHHMMSS(parseInt(seconds.toString(), 10));
                    formContext.setValue(name, seconds === 0 ? '' : newTime);
                }
                inputOptions?.onBlur?.(event);
            },
            onChange: (value: string) => {
                // Number input.
                if (type === 'number') {
                    const strippedNumber = value.replace(/\D/g, '');
                    formContext.setValue(name, strippedNumber);
                }
                // Phone number input.
                else if (type === 'phone') {
                    const strippedPhone = value.replace(/\D/g, '');
                    formContext.setValue(name, strippedPhone);
                }
                // Hour/Minute (time) input.
                else if (type === 'time') {
                    formContext.setValue(name, value);
                }
                // Zip code input.
                else if (type === 'zip') {
                    // Allow US (5 digits or 5+4 format) and Canadian (A1A 1A1) zip codes
                    const usZipRegex = /^\d{0,5}(-\d{0,4})?$/; // Matches US zip codes
                    const caZipRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d?$/; // Matches Canadian postal codes

                    // Remove invalid characters
                    const strippedZipCode = value.replace(/[^A-Za-z0-9 -]/g, '');

                    // Validate against US or Canadian formats
                    if (usZipRegex.test(strippedZipCode) || caZipRegex.test(strippedZipCode)) {
                        formContext.setValue(name, strippedZipCode.toUpperCase()); // Convert to uppercase for Canadian postal codes
                    }
                } else if (type === 'USzip') {
                    // US zip code input.
                    const usZipRegex = /^\d{0,5}(-\d{0,4})?$/; // Matches US zip codes

                    // Remove invalid characters
                    const strippedUSZipCode = value.replace(/[^0-9 -]/g, '');

                    // Validate against US format
                    if (usZipRegex.test(strippedUSZipCode)) {
                        formContext.setValue(name, strippedUSZipCode); // Keep US zip codes as is
                    }
                } else if (type === 'CAzip') {
                    // Canadian postal code input.
                    const caZipRegexPartial = /^[A-Za-z]?\d?[A-Za-z]?[ ]?\d?[A-Za-z]?\d?$/; // Allows partial Canadian postal codes
                    const caZipRegexFull = /^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/; // Matches full Canadian postal codes

                    // Remove invalid characters but allow letters, numbers, and spaces
                    const strippedCAZipCode = value.replace(/[^A-Za-z0-9 ]/g, '');

                    // Format the input to match the Canadian postal code format
                    let formattedCAZipCode = strippedCAZipCode.toUpperCase();
                    if (formattedCAZipCode.length > 3 && !formattedCAZipCode.includes(' ')) {
                        formattedCAZipCode = `${formattedCAZipCode.slice(0, 3)} ${formattedCAZipCode.slice(3)}`;
                    }

                    // Allow partial input and set value
                    if (caZipRegexPartial.test(formattedCAZipCode)) {
                        formContext.setValue(name, formattedCAZipCode);

                        // Show error if input is 7 characters and not a valid full postal code
                        if (formattedCAZipCode.length === 7 && !caZipRegexFull.test(formattedCAZipCode)) {
                            formContext.setError(name, {
                                type: 'manual',
                                message: localization.translate('validation.invalidZipCode'),
                            });
                        } else if (caZipRegexFull.test(formattedCAZipCode)) {
                            formContext.clearErrors(name); // Clear errors if valid
                        }
                    } else {
                        // If not even a partial match, show error
                        formContext.setError(name, {
                            type: 'manual',
                            message: localization.translate('validation.invalidZipCode'),
                        });
                    }
                }

                // Other inputs.
                else formContext.setValue(name, value);

                if (errorMessage) formContext.clearErrors(name);
                inputOptions?.onChange?.(value);
            },
            isDisabled: inputOptions?.isDisabled,
        },
        inputRef
    );

    const errorMessage: string = formContext.formState.errors[name]?.message?.toString() ?? '';
    const hasCheckbox: boolean = checkboxOptions !== undefined;
    const hasError: boolean = errorMessage.length > 0;
    const isClearShowing: boolean = !inputOptions?.isDisabled && !inputOptions?.isReadOnly && formValue;

    const inputValue: string =
        type === 'phone'
            ? formValue !== 'Unknown'
                ? formatPhoneNumber(formValue, '')
                : formValue ?? ''
            : formValue ?? '';

    const hasValue = inputValue.length > 0;

    return (
        <div className={'flex w-full flex-col'}>
            {/* LABEL */}
            <label {...labelProps}>
                <Tooltip hasError={hasError} isRequired={inputOptions?.isRequired} tooltip={tooltip}>
                    {inputOptions?.label}
                </Tooltip>
            </label>

            {/* INPUT FIELD */}
            <div className={'flex flex-row items-center'}>
                {/* INPUT */}
                <div className={'relative w-full'}>
                    <input
                        {...inputProps}
                        className={clsx(
                            'input-primary h-10',
                            inputOptions?.isDisabled && 'cursor-not-allowed text-gray-400 dark:text-gray-400',
                            hasError && 'input-error',
                            inputOptions?.isRequired && !hasValue && 'bg-red-100 dark:bg-red-200/80',
                            className
                        )}
                        disabled={inputOptions?.isDisabled}
                        ref={(ref) => {
                            if (ref === null) return;
                            inputRef.current = ref;
                            formRef(ref);
                        }}
                        value={inputValue}
                    />

                    {/* CLEAR BUTTON */}
                    {isClearShowing && (
                        <InputClearButton inputRef={inputRef} name={name} onChange={inputOptions?.onChange} />
                    )}
                </div>

                {/* UNKNOWN CHECKBOX */}
                {hasCheckbox && (
                    <div className={'-mt-1 pl-2'}>
                        <Checkbox hasError={hasError} options={checkboxOptions}>
                            <div className={'font-semibold'}>{localization.translate('dropdownOptions.unknown')}</div>
                        </Checkbox>
                    </div>
                )}
            </div>

            {/* ERROR MESSAGE */}
            {hasError && <ErrorMessage ariaProps={errorMessageProps}>{errorMessage}</ErrorMessage>}
        </div>
    );
};
